'use client';

import { Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface UserCountSelectorProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  colorScheme?: 'green' | 'blue';
}

export function UserCountSelector({
  value,
  onChange,
  min = 1,
  max = 100,
  colorScheme = 'blue',
}: UserCountSelectorProps) {
  const handleDecrement = () => {
    if (value > min) {
      onChange(value - 1);
    }
  };

  const handleIncrement = () => {
    if (value < max) {
      onChange(value + 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10);
    if (!isNaN(newValue) && newValue >= min && newValue <= max) {
      onChange(newValue);
    }
  };

  const buttonClasses = cn(
    'w-10 h-10 rounded-full flex items-center justify-center transition-all',
    colorScheme === 'blue'
      ? 'bg-primaryBlue text-white hover:bg-primaryBlue/80'
      : 'bg-primaryGreen text-white hover:bg-primaryGreen/80',
    'disabled:opacity-50 disabled:cursor-not-allowed'
  );

  return (
    <div className="flex items-center justify-center gap-4">
      <span className="text-sm font-medium text-gray-700">Nombre d'utilisateurs :</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleDecrement}
          disabled={value <= min}
          className={buttonClasses}
          aria-label="Diminuer le nombre d'utilisateurs"
        >
          <Minus className="w-4 h-4" />
        </button>
        
        <input
          type="number"
          value={value}
          onChange={handleInputChange}
          min={min}
          max={max}
          className={cn(
            'w-16 h-10 text-center text-lg font-semibold border-2 rounded-lg',
            colorScheme === 'blue'
              ? 'border-primaryBlue focus:ring-primaryBlue'
              : 'border-primaryGreen focus:ring-primaryGreen',
            'focus:outline-none focus:ring-2'
          )}
        />
        
        <button
          type="button"
          onClick={handleIncrement}
          disabled={value >= max}
          className={buttonClasses}
          aria-label="Augmenter le nombre d'utilisateurs"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
